import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import api from '../../../services/api';

function formatAmount(value) {
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Agrupa por categoria do Scheduling (a function já manda cada item classificado)
// e soma por moeda — moedas diferentes nunca se somam.
function groupByCategory(items) {
  const groups = new Map();
  items.forEach(item => {
    const categoryId = item.IDSellValueTypeCategory;
    if (!groups.has(categoryId)) groups.set(categoryId, { items: [], totals: new Map() });
    const group = groups.get(categoryId);
    group.items.push(item);

    const currency = item.CurrencyAbbreviation || `#${item.IDCurrency}`;
    group.totals.set(currency, (group.totals.get(currency) || 0) + Math.abs(Number(item.TotalValue)));
  });
  return groups;
}

function ItemsTooltip({ items }) {
  return (
    <div>
      {items.map(item => (
        <div key={item.IDPricingScenarioItem}>
          {item.ItemName}
          {item.Description ? ` — ${item.Description}` : ''}:{' '}
          {item.CurrencyAbbreviation} {formatAmount(Math.abs(Number(item.TotalValue)))}
        </div>
      ))}
    </div>
  );
}

// Custos adicionais vindos da venda (tabela ProjectPendingCreateCost, gravada pela
// function-scheduling): uma linha por tipo de custo, só quando há custo. Só chegam
// despesas embutidas de valor > 0 — exatamente o que a aprovação/anexação lança.
export default function PendingProjectCosts({ costs }) {
  const items = costs || [];
  const [categoryNames, setCategoryNames] = useState(new Map());

  useEffect(() => {
    if (items.length === 0) return;
    api
      .get('scheduling/sell-value-types-categories')
      .then(response =>
        setCategoryNames(new Map(response.data.map(c => [c.ID, c.Description]))),
      )
      .catch(() => setCategoryNames(new Map()));
  }, [items.length]);

  const groups = useMemo(() => groupByCategory(items), [items]);

  if (items.length === 0) return null;

  const missingMain = items.some(
    item => item.TotalValueMain === null || item.TotalValueMain === undefined,
  );

  return (
    <>
      <Typography
        variant="body2"
        color="textSecondary"
        style={{ margin: '16px 12px 4px', fontWeight: 500 }}
      >
        Additional costs (from the sale)
      </Typography>

      {[...groups.entries()].map(([categoryId, group]) => (
        <Tooltip
          key={categoryId}
          title={<ItemsTooltip items={group.items} />}
          placement="left"
          arrow
        >
          <div className="project-info-box">
            <div className="list-item-content">
              <span className="secondary-text">
                {categoryNames.get(categoryId) || `Category ${categoryId}`}:{' '}
              </span>
              <span>
                {[...group.totals.entries()]
                  .map(([currency, total]) => `${currency} ${formatAmount(total)}`)
                  .join(' · ')}
              </span>
            </div>
          </div>
        </Tooltip>
      ))}

      {missingMain && (
        <Alert severity="info" style={{ margin: 5 }}>
          Some costs have no USD value from the sale yet — their USD value will be
          converted by the exchange rate of the quarter.
        </Alert>
      )}
    </>
  );
}
